import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Loader2, Send, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { useChat, type ChatMessage } from '@/hooks/useChat';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';

interface CoCeoSessionResponse {
  session: {
    id: string;
    subject: string;
    status: string;
  };
  persona: {
    key: string;
    name: string;
    role: string;
    alwaysOn: boolean;
  };
  summary: {
    strategyItems: number;
    fundraisingItems: number;
    dataroomItems: number;
  };
  starterPrompts: string[];
}

export function CeoCoCeoPanel() {
  const [session, setSession] = useState<CoCeoSessionResponse | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const convId = session?.session.id ?? null;
  const { messages, streaming, loading, loadMessages, sendMessage, mode } = useChat(convId);

  useEffect(() => {
    let active = true;
    async function ensureSession() {
      setLoadingSession(true);
      try {
        const data = await api.get<CoCeoSessionResponse>('/ceo-desk/co-ceo/session');
        if (!active) return;
        setSession(data);
      } finally {
        if (active) setLoadingSession(false);
      }
    }
    void ensureSession();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!convId) return;
    void loadMessages();
  }, [convId, loadMessages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const visibleMessages = useMemo(() => messages.slice(-12), [messages]);

  async function submitPrompt(prompt: string) {
    if (!convId || !prompt.trim() || streaming) return;
    setSending(true);
    try {
      await sendMessage(prompt.trim(), { targetConvId: convId });
      setInput('');
    } finally {
      setSending(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await submitPrompt(input);
  }

  if (loadingSession) {
    return (
      <section className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white">
        <div className="flex min-h-[240px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
        </div>
      </section>
    );
  }

  if (!session) {
    return (
      <section className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white">
        <div className="text-sm text-slate-300">AI Co-CEO session is unavailable.</div>
      </section>
    );
  }

  return (
    <section className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-slate-300">
            <Bot className="h-3.5 w-3.5" />
            {session.persona.name}
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">Always-on executive sparring partner</h2>
          <p className="max-w-3xl text-sm leading-6 text-slate-300">
            AI Co-CEO 会跟着 CEO Desk 常驻存在。你可以直接和它讨论战略、融资、data-room、runtime risk 和 board prep。
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
          <div>{session.persona.role}</div>
          <div className="mt-1">Mode: {mode ?? 'brain'}</div>
          <div className="mt-1">Strategy {session.summary.strategyItems} · Fundraising {session.summary.fundraisingItems} · Data-room {session.summary.dataroomItems}</div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[320px_1fr]">
        <div className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-[0.22em] text-slate-400">Quick starts</div>
          {session.starterPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => void submitPrompt(prompt)}
              disabled={streaming || sending}
              className="block w-full rounded-2xl border border-white/10 bg-white/5 p-4 text-left text-sm leading-6 text-slate-200 transition hover:border-white/20 hover:bg-white/10 disabled:opacity-50"
            >
              <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                <Sparkles className="h-3.5 w-3.5" />
                Prompt
              </div>
              <div className="mt-2">{prompt}</div>
            </button>
          ))}
        </div>

        <div className="rounded-3xl border border-white/10 bg-black/20">
          <div className="border-b border-white/10 px-5 py-4 text-sm text-slate-300">
            {session.session.subject} · persistent executive thread
          </div>

          <div className="max-h-[520px] space-y-4 overflow-y-auto px-5 py-5">
            {loading ? (
              <div className="text-sm text-slate-400">Loading conversation...</div>
            ) : visibleMessages.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-5 text-sm leading-6 text-slate-300">
                Start with a board question, funding update, strategic tradeoff, or “what am I missing?” review.
              </div>
            ) : visibleMessages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => void handleSubmit(e)} className="border-t border-white/10 p-5">
            <div className="flex gap-3">
              <textarea
                className="min-h-[104px] flex-1 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-white/20"
                placeholder="Ask the AI Co-CEO about strategy, runway, fundraising sequencing, dataroom gaps, or board narrative."
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <button
                type="submit"
                disabled={!input.trim() || streaming || sending}
                className="inline-flex h-fit items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-medium text-slate-950 disabled:opacity-50"
              >
                {(streaming || sending) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-6 ${isUser ? 'bg-white text-slate-950' : 'bg-white/5 text-slate-100'}`}>
        <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
          {isUser ? 'CEO' : 'AI Co-CEO'}
        </div>
        {isUser ? (
          <div className="whitespace-pre-wrap">{message.content}</div>
        ) : (
          <MarkdownRenderer content={message.content || '...'} />
        )}
      </div>
    </div>
  );
}
